import { useState } from "react";
import { api } from "../lib/api";
import { Checkbox } from "./ui/checkbox";
import { track } from "../lib/tracking";
import { useLang } from "../context/LanguageContext";
import { Loader2, Check, ArrowRight } from "lucide-react";

export function LeadForm({ course = null, source = "landing", simplified = false, onSuccess, accent = "#1452F0" }) {
  const { t } = useLang();
  const [form, setForm] = useState({
    name: "", surname: "", email: "", phone: "", city: "", profession: "", message: "",
  });
  const [privacy, setPrivacy] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!privacy) {
      setError(t("lead.err_privacy"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", {
        ...form,
        course_id: course?.id || "",
        course_title: course?.title || "",
        privacy_accepted: privacy,
        marketing_consent: marketing,
        source,
      });
      track("form_submit", { course: course?.title || "", source });
      setDone(true);
      onSuccess && onSuccess();
    } catch (err) {
      setError(t("lead.err_generic"));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="lead-success" className="py-10 text-center fade-up">
        <div
          className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full"
          style={{ backgroundColor: accent }}
        >
          <Check className="h-7 w-7 text-[#F4F8FF]" strokeWidth={1.5} />
        </div>
        <h3 className="font-heading text-3xl font-light text-[#0B1220]">{t("lead.success_title")}</h3>
        <p className="mx-auto mt-3 max-w-md text-[#5C6678] font-light leading-relaxed">
          {t("lead.success_msg")}
        </p>
      </div>
    );
  }

  return (
    <form data-testid="lead-form" onSubmit={submit} className="space-y-6">
      {course?.title && (
        <div>
          <label className="overline mb-2 block">{t("lead.course_interest")}</label>
          <div
            data-testid="lead-course-prefilled"
            className="font-heading text-xl text-[#0B1220] border-b border-[#0B1220]/15 pb-3"
          >
            {course.title}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label className="overline mb-1 block">{t("lead.name")} *</label>
          <input data-testid="lead-name" required value={form.name} onChange={upd("name")} className="editorial-input" placeholder={t("lead.ph_name")} />
        </div>
        <div>
          <label className="overline mb-1 block">{t("lead.surname")} *</label>
          <input data-testid="lead-surname" required value={form.surname} onChange={upd("surname")} className="editorial-input" placeholder={t("lead.ph_surname")} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label className="overline mb-1 block">{t("lead.email")} *</label>
          <input data-testid="lead-email" required type="email" value={form.email} onChange={upd("email")} className="editorial-input" placeholder={t("lead.ph_email")} />
        </div>
        <div>
          <label className="overline mb-1 block">{t("lead.phone")}</label>
          <input data-testid="lead-phone" value={form.phone} onChange={upd("phone")} className="editorial-input" placeholder={t("lead.ph_phone")} />
        </div>
      </div>

      {!simplified && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="overline mb-1 block">{t("lead.city")}</label>
              <input data-testid="lead-city" value={form.city} onChange={upd("city")} className="editorial-input" placeholder={t("lead.ph_city")} />
            </div>
            <div>
              <label className="overline mb-1 block">{t("lead.profession")}</label>
              <input data-testid="lead-profession" value={form.profession} onChange={upd("profession")} className="editorial-input" placeholder={t("lead.ph_profession")} />
            </div>
          </div>

          <div>
            <label className="overline mb-1 block">{t("lead.message")}</label>
            <textarea data-testid="lead-message" rows={3} value={form.message} onChange={upd("message")} className="editorial-input resize-none" placeholder={t("lead.ph_message")} />
          </div>
        </>
      )}

      <div className="space-y-3 pt-2">
        <label className="flex items-start gap-3 cursor-pointer">
          <Checkbox data-testid="lead-privacy" checked={privacy} onCheckedChange={setPrivacy} className="mt-0.5 data-[state=checked]:bg-[#1452F0] data-[state=checked]:border-[#1452F0]" />
          <span className="text-sm text-[#5C6678] leading-snug">
            {t("lead.privacy")}
          </span>
        </label>
        <label className="flex items-start gap-3 cursor-pointer">
          <Checkbox data-testid="lead-marketing" checked={marketing} onCheckedChange={setMarketing} className="mt-0.5 data-[state=checked]:bg-[#1452F0] data-[state=checked]:border-[#1452F0]" />
          <span className="text-sm text-[#5C6678] leading-snug">
            {t("lead.marketing")}
          </span>
        </label>
      </div>

      {error && <p data-testid="lead-error" className="text-sm text-[#A63C3C]">{error}</p>}

      <button
        data-testid="lead-submit"
        type="submit"
        disabled={loading}
        className="group w-full bg-[#1452F0] text-[#F4F8FF] hover:bg-[#0E3DC0] transition-colors rounded-sm px-8 py-4 text-sm font-medium tracking-wide flex items-center justify-center gap-2 disabled:opacity-60"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{t("common.send_request")} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></>}
      </button>
    </form>
  );
}
